import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkAuth,
  requestOtp,
  verifyOtp,
  requestEmailOtp,
  verifyEmailOtp,
  logout,
} from '../lib/auth';
import { acceptWebLegalConsent, completeWebOnboarding, recordWebAgeAttestation } from '../lib/onboarding';
import type {
  OtpRequestPayload,
  OtpVerifyPayload,
  EmailOtpRequestPayload,
  EmailOtpVerifyPayload,
  CompleteWebOnboardingPayload,
  AgeAttestationSelection,
} from '../types';

const ratingPilotQueryRoot = ['rating-pilot'] as const;

async function clearPrivateRatingPilotQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.cancelQueries({ queryKey: ratingPilotQueryRoot });
  queryClient.removeQueries({ queryKey: ratingPilotQueryRoot });
}

// ============================================
// Query Keys
// ============================================

export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

// ============================================
// useAuth Hook (Consumer)
// ============================================

export function useAuth() {
  const queryClient = useQueryClient();

  // Check authentication status
  const {
    data: user,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: authKeys.user(),
    queryFn: checkAuth,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Request OTP mutation
  const requestOtpMutation = useMutation({
    mutationFn: (payload: OtpRequestPayload) => requestOtp(payload),
  });

  // Verify OTP mutation
  const verifyOtpMutation = useMutation({
    mutationFn: (payload: OtpVerifyPayload) => verifyOtp(payload),
    onSuccess: async (data) => {
      await clearPrivateRatingPilotQueries(queryClient);
      // Update cached user after successful login
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });

  const requestEmailOtpMutation = useMutation({
    mutationFn: (payload: EmailOtpRequestPayload) => requestEmailOtp(payload),
  });

  const verifyEmailOtpMutation = useMutation({
    mutationFn: (payload: EmailOtpVerifyPayload) => verifyEmailOtp(payload),
    onSuccess: async (data) => {
      await clearPrivateRatingPilotQueries(queryClient);
      queryClient.setQueryData(authKeys.user(), data.user);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await clearPrivateRatingPilotQueries(queryClient);
      // Clear all auth-related cache
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isError,
    refetch,
    requestOtp: requestOtpMutation.mutateAsync,
    isRequestingOtp: requestOtpMutation.isPending,
    verifyOtp: verifyOtpMutation.mutateAsync,
    isVerifyingOtp: verifyOtpMutation.isPending,
    requestEmailOtp: requestEmailOtpMutation.mutateAsync,
    isRequestingEmailOtp: requestEmailOtpMutation.isPending,
    verifyEmailOtp: verifyEmailOtpMutation.mutateAsync,
    isVerifyingEmailOtp: verifyEmailOtpMutation.isPending,
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
  };
}

export function useWebOnboarding() {
  const queryClient = useQueryClient();

  const acceptLegalConsentMutation = useMutation({
    mutationFn: () => acceptWebLegalConsent(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });

  const attestAgeMutation = useMutation({
    mutationFn: (selection: AgeAttestationSelection) => recordWebAgeAttestation(selection),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });

  const completeOnboardingMutation = useMutation({
    mutationFn: (payload: CompleteWebOnboardingPayload) => completeWebOnboarding(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });

  return {
    acceptLegalConsent: acceptLegalConsentMutation.mutateAsync,
    isAcceptingLegalConsent: acceptLegalConsentMutation.isPending,
    attestAge: attestAgeMutation.mutateAsync,
    isAttestingAge: attestAgeMutation.isPending,
    completeOnboarding: completeOnboardingMutation.mutateAsync,
    isCompletingOnboarding: completeOnboardingMutation.isPending,
  };
}
